import React, { useCallback, useEffect, useMemo } from 'react';
import { Icon, MenuItem } from 'hfs-common-ui';
import useBooleanState from '~/hooks/use-boolean-state';
import MobileMenuDropwdown from '../MobileMenuDropwdown/MobileMenuDropwdown';
import * as S from './styled';

export interface Props {
  menuItem: MenuItem;
  activeId?: number;
  onClick: (menuItem: MenuItem) => void;
}

const MobileMenuItem = ({ menuItem, activeId, onClick }: Props) => {
  const { id, label, menuItems } = menuItem;
  const hasSubMenu = menuItems && menuItems.length > 0;
  const [isMenuExpanded, toggleIsMenuExpanded, setIsMenuExpanded] = useBooleanState(false);

  const isSelected = useMemo<boolean>(() => {
    if (activeId === id) {
      return true;
    }
    return false;
  }, [id, activeId]);

  useEffect(() => {
    if (hasSubMenu && menuItems.some((item) => item.id === activeId)) {
      setIsMenuExpanded(true);
    }
  }, [activeId, hasSubMenu, menuItems, setIsMenuExpanded]);

  const handleOnClick = useCallback(() => {
    if (hasSubMenu) {
      toggleIsMenuExpanded();
      return;
    }
    onClick(menuItem);
  }, [hasSubMenu, menuItem, onClick, toggleIsMenuExpanded]);

  return (
    <>
      <S.MenuItem $isSelected={isSelected} onClick={handleOnClick}>
        <S.Shield>
          <S.ShieldEdge>
            <path d="M 4.5342 3.669 C 4.8351 4.0289 5 4.483 5 4.9521 L 5 6 L 0 6 L -0 1.002 C -0 0.0676 1.1676 -0.3563 1.7671 0.3605 L 4.5342 3.669 Z" />
          </S.ShieldEdge>
          <S.ShieldCenter data-testid="menuShield" />
          <S.ShieldEdge>
            <path d="m 4.534 2.331 c 0.301 -0.36 0.466 -0.814 0.466 -1.283 l 0 -1.048 l -5 0 l 0 4.998 c 0 0.934 1.168 1.358 1.767 0.642 l 2.767 -3.309 z" />
          </S.ShieldEdge>
        </S.Shield>
        {menuItem.metadata?.icon && (
          <S.IconWrapper $placement="left">
            <Icon type={menuItem.metadata.icon} />
          </S.IconWrapper>
        )}
        <S.MenuLabel>{label}</S.MenuLabel>
        {hasSubMenu && (
          <S.IconWrapper $placement="right">
            {isMenuExpanded ? (
              <Icon type="action.arrow_up" size="small" />
            ) : (
              <Icon type="action.arrow_down" size="small" />
            )}
          </S.IconWrapper>
        )}
      </S.MenuItem>
      {hasSubMenu && (
        <S.DropdownContent $isExpanded={isMenuExpanded} data-testid="dropdownMobileNav">
          <MobileMenuDropwdown
            onClick={onClick}
            activeMenuItemId={activeId}
            menu={menuItem.menuItems}
          ></MobileMenuDropwdown>
        </S.DropdownContent>
      )}
    </>
  );
};

export default MobileMenuItem;
